import { useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Briefcase, ChevronDown, Calendar, Mail, LogIn, LogOut, LayoutDashboard } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { BookCallDialog } from "@/components/BookCallDialog";
import { useAuth } from "@/contexts/AuthContext";
import { useScrollSpy, scrollToSection } from "@/hooks/useScrollSpy";
const navigation = [{
  name: "About",
  href: "/about"
}, {
  name: "Services",
  href: "/services"
}, {
  name: "Portfolio",
  href: "/portfolio"
}, {
  name: "Forms",
  href: "/forms"
}, {
  name: "Contact",
  href: "/contact"
}];

// Sections for the homepage scroll spy
const homeSections = [{
  id: "hero",
  label: "Home"
}, {
  id: "why-choose",
  label: "Why Choose"
}, {
  id: "services",
  label: "Services"
}, {
  id: "testimonials",
  label: "Testimonials"
}, {
  id: "contact",
  label: "Contact"
}];
export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [bookCallOpen, setBookCallOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    user,
    role,
    signOut
  } = useAuth();
  const isHomePage = location.pathname === "/";
  const activeSection = useScrollSpy(isHomePage ? homeSections : [], 150);
  const handleLogout = async () => {
    await signOut();
    navigate("/");
  };
  const getDashboardLink = () => {
    if (role === "admin") return "/dashboard/admin";
    if (role === "secretary") return "/dashboard/secretary";
    return "/";
  };
  const handleSectionClick = useCallback((sectionId: string) => {
    if (isHomePage) {
      scrollToSection(sectionId, 80);
    } else {
      navigate("/");
      setTimeout(() => scrollToSection(sectionId, 80), 100);
    }
  }, [isHomePage, navigate]);
  return <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border/50">
        <nav className="container mx-auto flex items-center justify-between py-4 px-4 lg:px-8">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <motion.div whileHover={{
            scale: 1.05,
            rotate: 5
          }} className="p-2 rounded-lg bg-gradient-accent shadow-md group-hover:shadow-glow transition-shadow duration-300">
              <Briefcase className="h-5 w-5 text-accent-foreground" />
            </motion.div>
            <div className="flex flex-col">
              <span className="text-xl font-bold text-foreground">
                Vantage<span className="text-accent">Strategic</span>
              </span>
              <span className="text-xs text-muted-foreground -mt-1">by Hervé K</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-10">
            {navigation.map(item => <Link key={item.name} to={item.href} className={`text-base font-bold transition-colors duration-200 relative group ${location.pathname === item.href ? "text-accent" : "text-foreground hover:text-accent"}`}>
                {item.name}
                <span className={`absolute -bottom-1 left-0 h-0.5 bg-accent transition-all duration-300 ${location.pathname === item.href ? "w-full" : "w-0 group-hover:w-full"}`} />
              </Link>)}
          </div>

          {/* Desktop CTA with Dropdown */}
          <div className="hidden md:flex items-center gap-3">
            {user ? <>
                {(role === "admin" || role === "secretary") && <Button variant="outline" size="sm" className="gap-2" asChild>
                    <Link to={getDashboardLink()}>
                      <LayoutDashboard className="h-4 w-4" />
                      Dashboard
                    </Link>
                  </Button>}
                <Button variant="login" size="sm" className="gap-2" onClick={handleLogout}>
                  <LogOut className="h-4 w-4" />
                  Logout
                </Button>
              </> : <Button variant="login" size="sm" className="gap-2" asChild>
                <Link to="/login">
                  <LogIn className="h-4 w-4" />
                  Login
                </Link>
              </Button>}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="hero" size="sm" className="gap-1">
                  Get Started
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-popover border-border">
                <DropdownMenuItem onClick={() => setBookCallOpen(true)} className="flex items-center gap-2 cursor-pointer">
                  <Calendar className="h-4 w-4" />
                  Book a Call
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a href="mailto:herve@VantageStrategic.co" className="flex items-center gap-2 cursor-pointer">
                    <Mail className="h-4 w-4" />
                    Send an Email
                  </a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Mobile menu button */}
          <button type="button" aria-label={mobileMenuOpen ? "Close menu" : "Open menu"} className="md:hidden p-2 rounded-lg hover:bg-secondary transition-colors" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <AnimatePresence mode="wait">
              {mobileMenuOpen ? <motion.div key="close" initial={{
              rotate: -90,
              opacity: 0
            }} animate={{
              rotate: 0,
              opacity: 1
            }} exit={{
              rotate: 90,
              opacity: 0
            }} transition={{
              duration: 0.2
            }}>
                  <X className="h-6 w-6 text-foreground" />
                </motion.div> : <motion.div key="menu" initial={{
              rotate: 90,
              opacity: 0
            }} animate={{
              rotate: 0,
              opacity: 1
            }} exit={{
              rotate: -90,
              opacity: 0
            }} transition={{
              duration: 0.2
            }}>
                  <Menu className="h-6 w-6 text-foreground" />
                </motion.div>}
            </AnimatePresence>
          </button>
        </nav>

        {/* Homepage Section Navigation - Only shows on homepage */}
        {isHomePage && <div className="hidden md:block border-t border-border/30 bg-background/60 backdrop-blur-sm">
            <div className="container mx-auto px-4 lg:px-8">
              
            </div>
          </div>}

        {/* Mobile menu */}
        <AnimatePresence>
          {mobileMenuOpen && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: "auto"
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.3,
          ease: "easeInOut"
        }} className="md:hidden bg-background border-b border-border overflow-hidden">
              <div className="container mx-auto px-4 py-4 space-y-2">
                {/* Homepage sections for mobile */}
                {isHomePage && <div className="pb-3 mb-3 border-b border-border">
                    <p className="text-xs font-medium text-muted-foreground mb-2 px-4">Jump to Section</p>
                    <div className="flex flex-wrap gap-2 px-4">
                      {homeSections.map(section => <button key={section.id} onClick={() => {
                  handleSectionClick(section.id);
                  setMobileMenuOpen(false);
                }} className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${activeSection === section.id ? "bg-accent text-accent-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
                          {section.label}
                        </button>)}
                    </div>
                  </div>}
                
                {navigation.map((item, index) => <motion.div key={item.name} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.05
            }}>
                    <Link to={item.href} onClick={() => setMobileMenuOpen(false)} className={`block text-base font-medium py-3 px-4 rounded-lg transition-colors ${location.pathname === item.href ? "text-accent bg-accent/10" : "text-muted-foreground hover:bg-muted"}`}>
                      {item.name}
                    </Link>
                  </motion.div>)}
                <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2
            }} className="pt-4 border-t border-border space-y-3">
                  {user ? <>
                      {(role === "admin" || role === "secretary") && <Button variant="outline" className="w-full gap-2" asChild>
                          <Link to={getDashboardLink()} onClick={() => setMobileMenuOpen(false)}>
                            <LayoutDashboard className="h-4 w-4" />
                            Dashboard
                          </Link>
                        </Button>}
                      <Button variant="login" className="w-full gap-2" onClick={() => {
                  setMobileMenuOpen(false);
                  handleLogout();
                }}>
                        <LogOut className="h-4 w-4" />
                        Logout
                      </Button>
                    </> : <Button variant="login" className="w-full gap-2" asChild>
                      <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                        <LogIn className="h-4 w-4" />
                        Login
                      </Link>
                    </Button>}
                  <Button variant="hero" className="w-full" onClick={() => {
                setMobileMenuOpen(false);
                setBookCallOpen(true);
              }}>
                    <Calendar className="h-4 w-4" />
                    Book a Call
                  </Button>
                  <Button variant="outline" className="w-full" asChild>
                    <a href="mailto:herve@VantageStrategic.co">
                      <Mail className="h-4 w-4" />
                      Send an Email
                    </a>
                  </Button>
                </motion.div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </header>

      {/* Book Call Dialog */}
      <BookCallDialog open={bookCallOpen} onOpenChange={setBookCallOpen} />
    </>;
}